import { useTracker } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { SloganColumnProps } from '@modules/page-builder/types';
import { trackArticleViewProfileClick } from '~/modules/tracking/util/trackArticleProfessionalCta';
import { RevampRichText } from './RevampRichText';
import { RevampProfessionalCtas } from './RevampProfessionalCtas';

/**
 * - `standard`: 33rem slogan measure, View Profile / Contact pill pair.
 * - `fullWidth`: (Figma "Name=yes, CTA=yes" 2456-68624) same layout on a
 *   72.5rem measure, with a single outlined View Profile pill.
 * - `half`: the slogan half of the slogan + paragraph spread (Figma
 *   2479:72694) — the same 80px type on a narrower 535 measure.
 */
type SloganVariant = 'standard' | 'fullWidth' | 'half';

interface RevampSloganModuleProps {
    column: SloganColumnProps;
    professional?: { title: string; link: string };
    variant?: SloganVariant;
}

/**
 * Slogan module (Figma "Page Builder / Slogan"): left hairline, the slogan,
 * the professional's name as the attribution when the module's ShowFullName
 * toggle is on, and, when its ShowCtaButtons toggle is on, CTA buttons for that
 * professional — the View Profile / Contact pill pair, or a single View Profile
 * pill in the full-width variant.
 *
 * Both are derived from the article's professional rather than page-builder
 * content, and the two toggles are independent, so an author can credit the
 * professional without the pills or vice versa. The attribution links to their
 * detail page.
 */
export const RevampSloganModule = ({
    column,
    professional,
    variant = 'standard',
}: RevampSloganModuleProps) => {
    const tracker = useTracker();
    // The attribution is the professional's own name, not authored copy, so
    // there is nothing to type in the editor. Articles with no professional are
    // ArchiPro's own editorial.
    const attributionName = professional?.title || 'ArchiPro';

    return (
        <div className="w-full">
            <div className="ml-6 flex flex-col items-start gap-8 border-l border-solid border-black/20 pl-5 md:ml-[8.0625rem] md:gap-[3.875rem] md:pl-[1.5625rem]">
                <RevampRichText
                    contentHTML={column.contentHTML}
                    className={cn(
                        'w-full font-sans text-[1.75rem] leading-[1.1] md:max-w-full md:leading-[1.1] md:tracking-[-0.03em]',
                        variant === 'standard' && 'md:w-[33rem] md:text-[5rem]',
                        variant === 'fullWidth' &&
                            'md:w-[72.5rem] md:text-[5rem]',
                        variant === 'half' &&
                            'md:w-[33.4619rem] md:text-[5rem]',
                    )}
                />
                {column.showFullName && (
                    <p className="m-0 font-sans text-[1.25rem] leading-[1.3] text-black opacity-80 md:text-[2rem] md:tracking-[-0.03em]">
                        {/* Only linked when there is a profile to reach; an
                            ArchiPro editorial article has no professional. */}
                        {professional?.link ? (
                            <RoccoLink
                                to={professional.link}
                                onClick={() =>
                                    trackArticleViewProfileClick(
                                        tracker,
                                        'sloganAttribution',
                                    )
                                }
                                className="text-inherit transition-opacity hover:opacity-80"
                            >
                                ({attributionName})
                            </RoccoLink>
                        ) : (
                            `(${attributionName})`
                        )}
                    </p>
                )}
                {column.showCtaButtons &&
                    professional &&
                    (variant === 'fullWidth' ? (
                        <RoccoLink
                            to={professional.link}
                            onClick={() =>
                                trackArticleViewProfileClick(tracker, 'slogan')
                            }
                            className="inline-flex items-center justify-center rounded-full border border-solid border-[#181818] font-sans leading-[1.3] font-medium whitespace-nowrap text-[#181818] shadow-[0_0.2rem_0.7rem_rgba(0,0,0,0.11)] transition-opacity hover:opacity-80 max-md:h-10 max-md:px-5 max-md:text-sm md:h-[3.25rem] md:px-[1.7225rem] md:text-[1rem]"
                        >
                            View Profile
                        </RoccoLink>
                    ) : (
                        <RevampProfessionalCtas
                            professional={professional}
                            placement="slogan"
                        />
                    ))}
            </div>
        </div>
    );
};
